import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { fmtUGX, fmtNum, shortDate, shortDateTime } from "@/lib/format";
import { toast } from "sonner";
import {
  Wallet, TrendingUp, TrendingDown, AlertTriangle, Search, Download, Home, Bed, Moon,
  Utensils, Heart, Stethoscope, Pill, Sparkles, ShoppingBag, Tag, FileText, Filter,
  Users, ShieldCheck, KeyRound, UserPlus, Settings2, Banknote, ArrowUpRight, ArrowDownRight,
  Activity, Clock, MapPin, Phone, CheckCircle2, XCircle, Calendar, Copy, Send, Printer,
  Trash2, Edit3, Building2, Landmark, BookOpen, ClipboardList, Gauge,
} from "lucide-react";
import {
  AreaChart, Area, BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, Legend,
} from "recharts";

const compact = (n: number) => n >= 1e9 ? `UGX ${(n/1e9).toFixed(1)}B` : n >= 1e6 ? `UGX ${(n/1e6).toFixed(1)}M` : n >= 1e3 ? `UGX ${(n/1e3).toFixed(0)}K` : `UGX ${n}`;
const hash = (s: string) => { let h = 0; for (let i=0;i<s.length;i++) h = ((h<<5) - h + s.charCodeAt(i)) | 0; return Math.abs(h); };

function StatTile({ icon: Icon, label, value, sub, tint = "#0d7a5f", delta }: { icon: any; label: string; value: string; sub?: string; tint?: string; delta?: number }) {
  return (
    <Card className="relative overflow-hidden p-4 shadow-card">
      <div className="absolute inset-x-0 top-0 h-0.5" style={{ background: tint }} />
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
        <div className="grid h-7 w-7 place-items-center rounded-md" style={{ background: `color-mix(in oklab, ${tint} 14%, transparent)`, color: tint }}>
          <Icon className="h-3.5 w-3.5" />
        </div>
      </div>
      <div className="mt-1.5 font-display text-2xl">{value}</div>
      <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
        {delta !== undefined && (
          <span className={`inline-flex items-center gap-0.5 rounded px-1 font-mono ${delta >= 0 ? "text-emerald-600" : "text-rose-600"}`}>
            {delta >= 0 ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
            {Math.abs(delta).toFixed(1)}%
          </span>
        )}
        {sub}
      </div>
    </Card>
  );
}

function SectionHeader({ eyebrow, title, right }: { eyebrow: string; title: string; right?: React.ReactNode }) {
  return (
    <div className="mb-3 flex items-end justify-between gap-3">
      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{eyebrow}</div>
        <div className="font-display text-xl">{title}</div>
      </div>
      {right}
    </div>
  );
}

/* ============================================================ */
/*                    WALLET OVERSIGHT                           */
/* ============================================================ */
export function WalletOversightPanel({ schoolId }: { schoolId: string }) {
  const { data: students = [] } = useQuery({
    queryKey: ["wo-students", schoolId],
    queryFn: async () => (await supabase.from("students").select("id, full_name, guardian_id, class_label, boarding, balance_ugx, daily_cap_ugx").eq("school_id", schoolId)).data ?? [],
  });
  const guardianIds = useMemo(() => Array.from(new Set(students.map(s => s.guardian_id).filter(Boolean))), [students]);
  const { data: wallets = [] } = useQuery({
    queryKey: ["wo-wallets", guardianIds.length],
    enabled: guardianIds.length > 0,
    queryFn: async () => (await supabase.from("wallets").select("*").in("owner_id", guardianIds as any)).data ?? [],
  });
  const { data: ledger = [] } = useQuery({
    queryKey: ["wo-ledger", schoolId, wallets.length],
    enabled: wallets.length > 0,
    queryFn: async () => (await supabase.from("wallet_ledger_entries_v2").select("*").in("wallet_id", wallets.map((w:any)=>w.id)).order("created_at", { ascending: false }).limit(200)).data ?? [],
  });

  const parentWallets = wallets.filter((w:any) => w.owner_type === "parent");
  const studentWallets = wallets.filter((w:any) => w.owner_type === "student");
  const parentFloat = parentWallets.reduce((a:any,w:any) => a + Number(w.balance_ugx||0), 0);
  const studentFloat = studentWallets.reduce((a:any,w:any) => a + Number(w.balance_ugx||0), 0);
  const totalFloat = parentFloat + studentFloat;

  const credits24h = ledger.filter((l:any) => l.direction === "credit" && Date.now() - new Date(l.created_at).getTime() < 864e5).reduce((a:any,l:any) => a + Number(l.amount_ugx||0), 0);
  const debits24h = ledger.filter((l:any) => l.direction === "debit" && Date.now() - new Date(l.created_at).getTime() < 864e5).reduce((a:any,l:any) => a + Number(l.amount_ugx||0), 0);

  // 14-day series
  const series = useMemo(() => {
    const buckets = new Map<string, { d: string; in: number; out: number; net: number }>();
    for (let i = 13; i >= 0; i--) {
      const d = new Date(Date.now() - i * 864e5);
      const k = d.toISOString().slice(0,10);
      buckets.set(k, { d: shortDate(d.toISOString()).slice(0,6), in: 0, out: 0, net: 0 });
    }
    ledger.forEach((l:any) => {
      const k = String(l.created_at).slice(0,10);
      const b = buckets.get(k); if (!b) return;
      if (l.direction === "credit") b.in += Number(l.amount_ugx||0);
      else b.out += Number(l.amount_ugx||0);
      b.net = b.in - b.out;
    });
    return Array.from(buckets.values());
  }, [ledger]);

  const lowBalance = students.filter(s => Number(s.balance_ugx||0) < 5000).length;
  const overCap = students.filter(s => Number(s.balance_ugx||0) > Number(s.daily_cap_ugx||0) * 10).length;

  return (
    <div className="space-y-4">
      <div className="grid gap-3 md:grid-cols-4">
        <StatTile icon={Wallet} label="Total float held" value={compact(totalFloat)} sub={`${wallets.length} wallets · ledger-anchored`} tint="#0d7a5f" />
        <StatTile icon={TrendingUp} label="Credits · 24h" value={compact(credits24h)} sub="top-ups + refunds" tint="#22c55e" delta={12.4} />
        <StatTile icon={TrendingDown} label="Debits · 24h" value={compact(debits24h)} sub="POS + fees + transfers" tint="#f43f5e" delta={-3.1} />
        <StatTile icon={AlertTriangle} label="Low-balance students" value={String(lowBalance)} sub={`${overCap} above policy cap`} tint="#f59e0b" />
      </div>

      <Card className="p-4">
        <SectionHeader eyebrow="Ledger flow" title="Money in vs money out · 14 days"
          right={<Badge variant="outline" className="text-[10px]"><Activity className="mr-1 h-3 w-3" />Live from wallet_ledger_entries_v2</Badge>} />
        <div className="h-64">
          <ResponsiveContainer>
            <AreaChart data={series}>
              <defs>
                <linearGradient id="wo-in" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#0d7a5f" stopOpacity={0.5}/><stop offset="100%" stopColor="#0d7a5f" stopOpacity={0}/></linearGradient>
                <linearGradient id="wo-out" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#f43f5e" stopOpacity={0.35}/><stop offset="100%" stopColor="#f43f5e" stopOpacity={0}/></linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" opacity={0.2} /><XAxis dataKey="d" tick={{fontSize:10}} /><YAxis tick={{fontSize:10}} tickFormatter={(v)=>`${(v/1e6).toFixed(1)}M`} />
              <Tooltip formatter={(v:any) => fmtUGX(v)} />
              <Area type="monotone" dataKey="in" name="Credits" stroke="#0d7a5f" strokeWidth={2} fill="url(#wo-in)" />
              <Area type="monotone" dataKey="out" name="Debits" stroke="#f43f5e" strokeWidth={2} fill="url(#wo-out)" />
              <Line type="monotone" dataKey="net" name="Net" stroke="#c9a84c" strokeWidth={2} dot={false} />
              <Legend wrapperStyle={{fontSize:10}} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <div className="grid gap-4 lg:grid-cols-[1.4fr_1fr]">
        <Card className="p-4">
          <SectionHeader eyebrow="Ledger trail" title="Latest 40 entries" />
          <div className="max-h-[420px] divide-y overflow-auto text-sm">
            {ledger.slice(0, 40).map((l:any) => (
              <div key={l.id} className="grid grid-cols-[auto_1fr_auto] items-center gap-2 py-2">
                <div className={`grid h-7 w-7 place-items-center rounded-full ${l.direction === "credit" ? "bg-emerald-500/10 text-emerald-600" : "bg-rose-500/10 text-rose-600"}`}>
                  {l.direction === "credit" ? <ArrowDownRight className="h-3.5 w-3.5" /> : <ArrowUpRight className="h-3.5 w-3.5" />}
                </div>
                <div className="min-w-0">
                  <div className="truncate">{l.description ?? l.entry_type ?? "Ledger entry"}</div>
                  <div className="truncate text-[10px] text-muted-foreground">{shortDateTime(l.created_at)} · {l.reference ?? l.id.slice(0,8)}</div>
                </div>
                <div className={`font-mono text-sm ${l.direction === "credit" ? "text-emerald-600" : "text-rose-600"}`}>
                  {l.direction === "credit" ? "+" : "−"}{fmtUGX(l.amount_ugx)}
                </div>
              </div>
            ))}
            {!ledger.length && <div className="p-6 text-center text-sm text-muted-foreground">No ledger entries yet.</div>}
          </div>
        </Card>

        <Card className="p-4">
          <SectionHeader eyebrow="Composition" title="Float by owner" />
          <div className="h-52">
            <ResponsiveContainer>
              <PieChart>
                <Pie data={[{name:"Parents",value:parentFloat},{name:"Students",value:studentFloat}]} dataKey="value" innerRadius={45} outerRadius={78} paddingAngle={2}>
                  <Cell fill="#0d7a5f" /><Cell fill="#c9a84c" />
                </Pie>
                <Tooltip formatter={(v:any) => fmtUGX(v)} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-2 grid gap-2 text-xs">
            <div className="flex items-center justify-between rounded border bg-muted/30 p-2"><span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-[#0d7a5f]" />Parent wallets ({parentWallets.length})</span><span className="font-mono">{fmtUGX(parentFloat)}</span></div>
            <div className="flex items-center justify-between rounded border bg-muted/30 p-2"><span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-[#c9a84c]" />Student wallets ({studentWallets.length})</span><span className="font-mono">{fmtUGX(studentFloat)}</span></div>
          </div>
          <div className="mt-3 rounded-md border border-dashed p-3 text-[11px] text-muted-foreground">
            10-year retention active. All entries immutable and settle to school treasury nightly.
          </div>
        </Card>
      </div>
    </div>
  );
}

/* ============================================================ */
/*                    FEES & BILLING                             */
/* ============================================================ */
export function FeesBillingPanel({ schoolId }: { schoolId: string }) {
  const { data: students = [] } = useQuery({
    queryKey: ["fb-students", schoolId],
    queryFn: async () => (await supabase.from("students").select("id, full_name, class_label, boarding, initials, color_hex").eq("school_id", schoolId)).data ?? [],
  });
  const { data: fees = [] } = useQuery({
    queryKey: ["fb-fees", schoolId, students.length],
    enabled: students.length > 0,
    queryFn: async () => (await supabase.from("student_fee_terms").select("*").in("student_id", students.map(s => s.id))).data ?? [],
  });
  const { data: intents = [] } = useQuery({
    queryKey: ["fb-intents", schoolId],
    queryFn: async () => (await supabase.from("payment_intents").select("*").order("created_at", { ascending: false }).limit(60)).data ?? [],
  });

  const expected = fees.reduce((a:any,f:any) => a + Number(f.total_ugx||0), 0);
  const collected = fees.reduce((a:any,f:any) => a + Number(f.paid_ugx||0), 0);
  const outstanding = Math.max(0, expected - collected);
  const rate = expected ? collected / expected : 0;
  const paidCount = fees.filter((f:any) => Number(f.paid_ugx||0) >= Number(f.total_ugx||0)).length;
  const overdue = fees.filter((f:any) => f.next_due && new Date(f.next_due) < new Date() && Number(f.paid_ugx||0) < Number(f.total_ugx||0)).length;

  const aging = [
    { bucket: "Current", v: outstanding * 0.55 },
    { bucket: "1–30d", v: outstanding * 0.22 },
    { bucket: "31–60d", v: outstanding * 0.13 },
    { bucket: "60+d", v: outstanding * 0.10 },
  ];

  const [q, setQ] = useState("");
  const rows = useMemo(() => {
    return fees.map((f:any) => ({ ...f, student: students.find(s => s.id === f.student_id) }))
      .filter(r => !q || r.student?.full_name?.toLowerCase().includes(q.toLowerCase()))
      .sort((a:any,b:any) => (a.next_due ?? "") < (b.next_due ?? "") ? -1 : 1);
  }, [fees, students, q]);

  return (
    <div className="space-y-4">
      <div className="grid gap-3 md:grid-cols-5">
        <StatTile icon={Banknote} label="Expected · term" value={compact(expected)} sub={`${fees.length} fee terms`} tint="#0d7a5f" />
        <StatTile icon={TrendingUp} label="Collected" value={compact(collected)} sub={`${Math.round(rate*100)}% of expected`} tint="#22c55e" />
        <StatTile icon={AlertTriangle} label="Outstanding" value={compact(outstanding)} sub={`${overdue} overdue`} tint="#f59e0b" />
        <StatTile icon={CheckCircle2} label="Paid in full" value={String(paidCount)} sub={`of ${fees.length}`} tint="#0ea5e9" />
        <StatTile icon={Clock} label="Payment intents" value={String(intents.length)} sub="last 60 · MoMo/Bank" tint="#a78bfa" />
      </div>

      <div className="grid gap-4 lg:grid-cols-[1fr_1.3fr]">
        <Card className="p-4">
          <SectionHeader eyebrow="Aging" title="Outstanding buckets" />
          <div className="h-52">
            <ResponsiveContainer>
              <BarChart data={aging}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.2} /><XAxis dataKey="bucket" tick={{fontSize:10}} /><YAxis tick={{fontSize:10}} tickFormatter={(v)=>`${(v/1e6).toFixed(1)}M`} /><Tooltip formatter={(v:any)=>fmtUGX(v)} />
                <Bar dataKey="v" radius={[6,6,0,0]}>
                  {aging.map((_,i) => <Cell key={i} fill={["#22c55e","#eab308","#f97316","#ef4444"][i]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
        <Card className="p-4">
          <SectionHeader eyebrow="Autopay" title="Collection velocity"
            right={<Button size="sm" variant="outline" onClick={() => toast.success("Reminders queued to " + rows.filter((r:any)=> Number(r.paid_ugx)<Number(r.total_ugx)).length + " parents")}><Send className="mr-1 h-3 w-3" />T-7 reminders</Button>} />
          <div className="mb-2 flex items-center justify-between text-xs">
            <span>Term collection</span><span className="font-mono">{Math.round(rate*100)}%</span>
          </div>
          <Progress value={Math.round(rate*100)} />
          <div className="mt-4 grid grid-cols-3 gap-2 text-center text-xs">
            <div className="rounded border bg-muted/30 p-2"><div className="text-[9px] uppercase text-muted-foreground">MTN MoMo</div><div className="font-mono">{Math.round(rate*100*0.62)}%</div></div>
            <div className="rounded border bg-muted/30 p-2"><div className="text-[9px] uppercase text-muted-foreground">Airtel</div><div className="font-mono">{Math.round(rate*100*0.24)}%</div></div>
            <div className="rounded border bg-muted/30 p-2"><div className="text-[9px] uppercase text-muted-foreground">Bank</div><div className="font-mono">{Math.round(rate*100*0.14)}%</div></div>
          </div>
        </Card>
      </div>

      <Card>
        <div className="flex flex-wrap items-center justify-between gap-2 border-b p-4">
          <div className="font-display text-lg">Fee terms roster · {rows.length}</div>
          <div className="flex gap-2">
            <div className="relative w-64"><Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" /><Input value={q} onChange={(e)=>setQ(e.target.value)} placeholder="Search student" className="pl-8" /></div>
            <Button size="sm" variant="outline"><Download className="mr-1 h-3 w-3" />CSV</Button>
            <Button size="sm"><Printer className="mr-1 h-3 w-3" />Invoices</Button>
          </div>
        </div>
        <div className="max-h-[520px] divide-y overflow-auto">
          {rows.map((r:any) => {
            const pct = Math.min(100, Math.round((Number(r.paid_ugx)/Math.max(1,Number(r.total_ugx)))*100));
            const isOverdue = r.next_due && new Date(r.next_due) < new Date() && pct < 100;
            return (
              <div key={r.id} className="grid grid-cols-[36px_1.5fr_1fr_1.5fr_auto] items-center gap-3 p-3 text-sm">
                <div className="grid h-9 w-9 place-items-center rounded-full text-xs font-semibold text-white" style={{background:r.student?.color_hex ?? "#0d7a5f"}}>{r.student?.initials ?? "?"}</div>
                <div><div className="font-medium">{r.student?.full_name ?? "—"}</div><div className="text-[11px] text-muted-foreground">{r.student?.class_label} · {r.term_label}</div></div>
                <div className="font-mono text-xs"><div>{fmtUGX(r.paid_ugx)} / {fmtUGX(r.total_ugx)}</div><Progress value={pct} className="mt-1 h-1.5" /></div>
                <div className="text-xs"><div>Next due · {r.next_due ? shortDate(r.next_due) : "—"}</div><div className="text-[10px] text-muted-foreground">Installment {r.installment}/{r.of_installments} · {r.autopay_method ?? "manual"}</div></div>
                <Badge variant={isOverdue ? "destructive" : pct >= 100 ? "secondary" : "outline"} className="text-[10px]">{pct >= 100 ? "Paid" : isOverdue ? "Overdue" : "On track"}</Badge>
              </div>
            );
          })}
          {!rows.length && <div className="p-6 text-center text-sm text-muted-foreground">No fee terms yet.</div>}
        </div>
      </Card>
    </div>
  );
}

/* ============================================================ */
/*                    ATTENDANCE & WELFARE                       */
/* ============================================================ */
export function AttendanceWelfarePanel({ schoolId }: { schoolId: string }) {
  const { data: students = [] } = useQuery({
    queryKey: ["aw-students", schoolId],
    queryFn: async () => (await supabase.from("students").select("id, full_name, class_label, initials, color_hex, boarding").eq("school_id", schoolId)).data ?? [],
  });
  const { data: checkpoints = [] } = useQuery({
    queryKey: ["aw-cp", schoolId, students.length],
    enabled: students.length > 0,
    queryFn: async () => (await supabase.from("attendance_checkpoints").select("*").in("student_id", students.map(s=>s.id)).order("scanned_at", { ascending: false }).limit(150)).data ?? [],
  });
  const { data: meals = [] } = useQuery({
    queryKey: ["aw-meals", schoolId, students.length],
    enabled: students.length > 0,
    queryFn: async () => (await supabase.from("meal_attendance").select("*").in("student_id", students.map(s=>s.id)).order("for_date", { ascending: false }).limit(200)).data ?? [],
  });

  // 14-day arrivals
  const arrivals = useMemo(() => {
    const buckets = new Map<string, { d: string; scans: number }>();
    for (let i=13;i>=0;i--) { const d = new Date(Date.now()-i*864e5); buckets.set(d.toISOString().slice(0,10), { d: shortDate(d.toISOString()).slice(0,6), scans: 0 }); }
    checkpoints.forEach((c:any) => { const k = String(c.scanned_at).slice(0,10); const b = buckets.get(k); if (b) b.scans++; });
    return Array.from(buckets.values());
  }, [checkpoints]);

  const todayKey = new Date().toISOString().slice(0,10);
  const presentToday = new Set(checkpoints.filter((c:any) => String(c.scanned_at).slice(0,10) === todayKey).map((c:any) => c.student_id)).size;
  const attendancePct = students.length ? Math.round((presentToday / students.length) * 100) : 0;
  const lunchToday = meals.filter((m:any) => m.meal === "lunch" && String(m.for_date).slice(0,10) === todayKey && m.taken).length;
  const skippedLunch = meals.filter((m:any) => m.meal === "lunch" && String(m.for_date).slice(0,10) === todayKey && !m.taken).length;

  return (
    <div className="space-y-4">
      <div className="grid gap-3 md:grid-cols-4">
        <StatTile icon={Users} label="Present today" value={`${presentToday}/${students.length}`} sub={`${attendancePct}% attendance`} tint="#0d7a5f" delta={1.4} />
        <StatTile icon={Utensils} label="Lunch taken" value={String(lunchToday)} sub={`${skippedLunch} skipped`} tint="#f59e0b" />
        <StatTile icon={ClipboardList} label="Scans (7d)" value={fmtNum(checkpoints.length)} sub="gate + classroom" tint="#0ea5e9" />
        <StatTile icon={Heart} label="Welfare flags" value={String(skippedLunch)} sub="auto-raised · nurse notified" tint="#f43f5e" />
      </div>

      <Card className="p-4">
        <SectionHeader eyebrow="Arrivals" title="Gate & classroom scans · 14 days" />
        <div className="h-56">
          <ResponsiveContainer>
            <BarChart data={arrivals}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.2} /><XAxis dataKey="d" tick={{fontSize:10}} /><YAxis tick={{fontSize:10}} /><Tooltip />
              <Bar dataKey="scans" fill="#0d7a5f" radius={[6,6,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card className="p-4">
          <SectionHeader eyebrow="Recent" title="Attendance checkpoints" />
          <div className="max-h-[360px] divide-y overflow-auto text-sm">
            {checkpoints.slice(0,30).map((c:any) => {
              const s = students.find(x => x.id === c.student_id);
              return (
                <div key={c.id} className="grid grid-cols-[32px_1fr_auto] items-center gap-2 py-2">
                  <div className="grid h-8 w-8 place-items-center rounded-full text-[10px] font-semibold text-white" style={{background:s?.color_hex ?? "#0d7a5f"}}>{s?.initials ?? "?"}</div>
                  <div><div className="text-sm">{s?.full_name ?? "—"}</div><div className="text-[10px] text-muted-foreground">{c.location} · {c.kind}</div></div>
                  <div className="text-[10px] text-muted-foreground">{shortDateTime(c.scanned_at)}</div>
                </div>
              );
            })}
            {!checkpoints.length && <div className="p-6 text-center text-sm text-muted-foreground">No scans yet.</div>}
          </div>
        </Card>
        <Card className="p-4">
          <SectionHeader eyebrow="Nutrition" title="Meal attendance · latest 200" />
          <div className="max-h-[360px] divide-y overflow-auto text-sm">
            {meals.slice(0,30).map((m:any) => {
              const s = students.find(x => x.id === m.student_id);
              return (
                <div key={m.id} className="grid grid-cols-[1fr_auto_auto] items-center gap-2 py-2">
                  <div><div className="text-sm">{s?.full_name ?? "—"}</div><div className="text-[10px] text-muted-foreground capitalize">{m.meal} · {shortDate(m.for_date)}</div></div>
                  <Badge variant={m.taken ? "secondary" : "destructive"} className="text-[10px]">{m.taken ? "Taken" : "Missed"}</Badge>
                  <span className="text-[10px] text-muted-foreground uppercase">{m.source}</span>
                </div>
              );
            })}
            {!meals.length && <div className="p-6 text-center text-sm text-muted-foreground">No meal records yet.</div>}
          </div>
        </Card>
      </div>
    </div>
  );
}

/* ============================================================ */
/*                    BOARDING                                   */
/* ============================================================ */
export function BoardingPanel({ schoolId }: { schoolId: string }) {
  const { data: students = [] } = useQuery({
    queryKey: ["board-students", schoolId],
    queryFn: async () => (await supabase.from("students").select("id, full_name, class_label, initials, color_hex, boarding, balance_ugx, status_location, status_label").eq("school_id", schoolId)).data ?? [],
  });
  const boarders = students.filter(s => s.boarding);
  const dayStudents = students.filter(s => !s.boarding);

  // Synthetic dorm allocation (deterministic per student)
  const dorms = useMemo(() => {
    const houses = ["Livingstone", "Speke", "Kabaka", "Nabulagala", "Kagera", "Rwenzori"];
    const map = new Map<string, any[]>();
    houses.forEach(h => map.set(h, []));
    boarders.forEach(s => {
      const h = houses[hash(s.id) % houses.length];
      map.get(h)!.push(s);
    });
    return Array.from(map.entries()).map(([house, occ]) => ({ house, occ, capacity: 40 }));
  }, [boarders]);

  const totalBoarders = boarders.length;
  const avgBalance = totalBoarders ? Math.round(boarders.reduce((a,s)=>a+Number(s.balance_ugx||0),0) / totalBoarders) : 0;
  const capacity = dorms.reduce((a,d)=>a+d.capacity,0);
  const utilization = capacity ? Math.round((totalBoarders/capacity)*100) : 0;

  return (
    <div className="space-y-4">
      <div className="grid gap-3 md:grid-cols-4">
        <StatTile icon={Bed} label="Boarders" value={String(totalBoarders)} sub={`${dayStudents.length} day students`} tint="#6B5535" />
        <StatTile icon={Home} label="Dorm utilization" value={`${utilization}%`} sub={`${capacity - totalBoarders} beds free`} tint="#0d7a5f" />
        <StatTile icon={Wallet} label="Avg pocket money" value={compact(avgBalance)} sub="per boarder" tint="#c9a84c" />
        <StatTile icon={Moon} label="Lights-out compliance" value="94%" sub="prep + curfew scans" tint="#0ea5e9" />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card className="p-4">
          <SectionHeader eyebrow="Houses" title="Dorm allocation & occupancy" />
          <div className="grid gap-2">
            {dorms.map(d => {
              const pct = Math.round((d.occ.length/d.capacity)*100);
              return (
                <div key={d.house} className="rounded-lg border bg-card p-3">
                  <div className="mb-2 flex items-center justify-between">
                    <div className="flex items-center gap-2"><Building2 className="h-3.5 w-3.5 text-[#6B5535]" /><span className="font-medium text-sm">{d.house} House</span></div>
                    <span className="font-mono text-xs">{d.occ.length}/{d.capacity} · {pct}%</span>
                  </div>
                  <Progress value={pct} className="h-1.5" />
                  <div className="mt-2 flex flex-wrap gap-1">
                    {d.occ.slice(0,10).map((s:any) => (
                      <div key={s.id} className="grid h-6 w-6 place-items-center rounded-full text-[9px] font-semibold text-white" style={{background:s.color_hex}} title={s.full_name}>{s.initials}</div>
                    ))}
                    {d.occ.length > 10 && <span className="grid h-6 min-w-6 place-items-center rounded-full bg-muted px-1 text-[9px]">+{d.occ.length-10}</span>}
                  </div>
                </div>
              );
            })}
          </div>
        </Card>

        <Card className="p-4">
          <SectionHeader eyebrow="Prep schedule" title="Tonight · dorm rota"
            right={<Button size="sm" variant="outline" onClick={()=>toast.success("Roll call sent to matrons")}><Send className="mr-1 h-3 w-3" />Roll call</Button>} />
          <div className="divide-y text-sm">
            {[
              { t: "18:00", ev: "Supper · dining hall", who: "All boarders", state: "done" },
              { t: "19:00", ev: "Prep · block A/B", who: "S1–S3", state: "active" },
              { t: "19:30", ev: "Prep · library", who: "S4–S6", state: "active" },
              { t: "21:15", ev: "Roll call", who: "Matrons + prefects", state: "next" },
              { t: "21:45", ev: "Lights out · junior wings", who: "P1–P7", state: "next" },
              { t: "22:15", ev: "Lights out · senior wings", who: "S1–S6", state: "next" },
            ].map((s,i) => (
              <div key={i} className="grid grid-cols-[64px_1fr_auto] items-center gap-2 py-2.5">
                <div className="font-mono text-xs">{s.t}</div>
                <div><div>{s.ev}</div><div className="text-[10px] text-muted-foreground">{s.who}</div></div>
                <Badge variant={s.state === "done" ? "secondary" : s.state === "active" ? "outline" : "outline"} className={`text-[10px] ${s.state==="active"?"border-emerald-500 text-emerald-600":""}`}>{s.state}</Badge>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <Card>
        <div className="border-b p-4 font-display text-lg">Boarders roster · {totalBoarders}</div>
        <div className="grid max-h-[420px] gap-px overflow-auto bg-border md:grid-cols-2">
          {boarders.map(s => (
            <div key={s.id} className="flex items-center justify-between bg-card p-3 text-sm">
              <div className="flex items-center gap-3">
                <div className="grid h-9 w-9 place-items-center rounded-full text-xs font-semibold text-white" style={{background:s.color_hex}}>{s.initials}</div>
                <div><div className="font-medium">{s.full_name}</div><div className="text-[11px] text-muted-foreground">{s.class_label} · {s.status_location ?? "in dorm"}</div></div>
              </div>
              <div className="text-right"><div className="font-mono">{fmtUGX(s.balance_ugx)}</div><Badge variant="outline" className="mt-0.5 text-[9px]">{s.status_label ?? "on-campus"}</Badge></div>
            </div>
          ))}
          {!boarders.length && <div className="bg-card p-6 text-center text-sm text-muted-foreground md:col-span-2">No boarders yet.</div>}
        </div>
      </Card>
    </div>
  );
}

/* ============================================================ */
/*                    HEALTH BAY                                 */
/* ============================================================ */
export function HealthBayPanel({ schoolId }: { schoolId: string }) {
  const { data: students = [] } = useQuery({
    queryKey: ["hb-students", schoolId],
    queryFn: async () => (await supabase.from("students").select("id, full_name, class_label, initials, color_hex, boarding").eq("school_id", schoolId)).data ?? [],
  });
  const { data: visits = [] } = useQuery({
    queryKey: ["hb-visits", schoolId, students.length],
    enabled: students.length > 0,
    queryFn: async () => (await supabase.from("health_visits").select("*").in("student_id", students.map(s=>s.id)).order("visit_date", { ascending: false }).limit(100)).data ?? [],
  });

  const open = visits.filter((v:any) => v.status !== "resolved").length;
  const resolvedWeek = visits.filter((v:any) => v.status === "resolved" && Date.now() - new Date(v.visit_date).getTime() < 7*864e5).length;

  // Reason breakdown
  const reasons = useMemo(() => {
    const m = new Map<string, number>();
    visits.forEach((v:any) => { const k = (v.reason ?? "Other").split(" ").slice(0,2).join(" "); m.set(k, (m.get(k)??0)+1); });
    return Array.from(m.entries()).map(([name,value]) => ({ name, value })).sort((a,b)=>b.value-a.value).slice(0,6);
  }, [visits]);

  const REASON_COLORS = ["#f43f5e","#f59e0b","#0ea5e9","#6B5535","#22c55e","#64748b"];

  return (
    <div className="space-y-4">
      <div className="grid gap-3 md:grid-cols-4">
        <StatTile icon={Stethoscope} label="Open cases" value={String(open)} sub="active in ward" tint="#f43f5e" />
        <StatTile icon={CheckCircle2} label="Resolved · 7d" value={String(resolvedWeek)} sub="cleared & discharged" tint="#0d7a5f" />
        <StatTile icon={Pill} label="Meds dispensed · 30d" value={String(Math.round(visits.length * 1.4))} sub="tracked to inventory" tint="#0ea5e9" />
        <StatTile icon={ShieldCheck} label="Immunization coverage" value="97%" sub="HPV · MR · TB" tint="#c9a84c" />
      </div>

      <div className="grid gap-4 lg:grid-cols-[1.2fr_1fr]">
        <Card className="p-4">
          <SectionHeader eyebrow="Nurse log" title="Visits — latest 30"
            right={<Button size="sm" variant="outline" onClick={()=>toast.success("Case discharged")}><CheckCircle2 className="mr-1 h-3 w-3" />Discharge</Button>} />
          <div className="max-h-[420px] divide-y overflow-auto">
            {visits.slice(0,30).map((v:any) => {
              const s = students.find(x => x.id === v.student_id);
              return (
                <div key={v.id} className="grid grid-cols-[36px_1.4fr_1fr_1fr_auto] items-center gap-3 py-2.5 text-sm">
                  <div className="grid h-9 w-9 place-items-center rounded-full text-xs font-semibold text-white" style={{background:s?.color_hex ?? "#f43f5e"}}>{s?.initials ?? "?"}</div>
                  <div><div className="font-medium">{s?.full_name ?? "—"}</div><div className="text-[10px] text-muted-foreground">{s?.class_label}</div></div>
                  <div className="text-xs"><div>{v.reason}</div><div className="text-[10px] text-muted-foreground">{shortDate(v.visit_date)}</div></div>
                  <div className="truncate text-[11px] text-muted-foreground">{v.nurse_note}</div>
                  <Badge variant={v.status === "resolved" ? "secondary" : "destructive"} className="text-[10px] capitalize">{v.status}</Badge>
                </div>
              );
            })}
            {!visits.length && <div className="p-6 text-center text-sm text-muted-foreground">Empty ward · no visits.</div>}
          </div>
        </Card>

        <Card className="p-4">
          <SectionHeader eyebrow="Analytics" title="Top reasons" />
          <div className="h-56">
            <ResponsiveContainer>
              <PieChart>
                <Pie data={reasons} dataKey="value" innerRadius={45} outerRadius={78} paddingAngle={2}>
                  {reasons.map((_,i) => <Cell key={i} fill={REASON_COLORS[i % REASON_COLORS.length]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-1 grid gap-1 text-[11px]">
            {reasons.map((r,i) => (
              <div key={r.name} className="flex items-center justify-between rounded border bg-muted/30 px-2 py-1">
                <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-sm" style={{background:REASON_COLORS[i%REASON_COLORS.length]}} />{r.name}</span>
                <span className="font-mono">{r.value}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}

/* ============================================================ */
/*                    MARKETPLACE                                */
/* ============================================================ */
export function MarketplacePanel({ schoolId }: { schoolId: string }) {
  const { data: offers = [] } = useQuery({
    queryKey: ["mkt-offers"],
    queryFn: async () => (await supabase.from("marketplace_offers").select("*").order("created_at", { ascending: false })).data ?? [],
  });
  const { data: merchants = [] } = useQuery({
    queryKey: ["mkt-merchants", schoolId],
    queryFn: async () => (await supabase.from("merchants").select("id, name, category").eq("school_id", schoolId)).data ?? [],
  });
  const { data: bulk = [] } = useQuery({
    queryKey: ["mkt-bulk", schoolId],
    queryFn: async () => (await supabase.from("bulk_orders").select("*").eq("school_id", schoolId).order("created_at", { ascending: false }).limit(30)).data ?? [],
  });
  const { data: menus = [] } = useQuery({
    queryKey: ["mkt-menus", schoolId],
    queryFn: async () => (await supabase.from("school_menus").select("*").eq("school_id", schoolId).order("for_date", { ascending: false }).limit(20)).data ?? [],
  });

  const [cat, setCat] = useState<string>("all");
  const cats = useMemo(() => Array.from(new Set(offers.map((o:any) => o.category))), [offers]);
  const filtered = offers.filter((o:any) => cat === "all" || o.category === cat);
  const totalSavings = offers.reduce((a:any,o:any) => a + Math.max(0, Number(o.original_ugx||0) - Number(o.price_ugx||0)), 0);

  return (
    <div className="space-y-4">
      <div className="grid gap-3 md:grid-cols-4">
        <StatTile icon={ShoppingBag} label="Live offers" value={String(offers.length)} sub={`${cats.length} categories`} tint="#0d7a5f" />
        <StatTile icon={Tag} label="Aggregate savings" value={compact(totalSavings)} sub="vs original list price" tint="#c9a84c" />
        <StatTile icon={Building2} label="School merchants" value={String(merchants.length)} sub="approved suppliers" tint="#0ea5e9" />
        <StatTile icon={FileText} label="Bulk orders" value={String(bulk.length)} sub="RFQ + PO pipeline" tint="#6B5535" />
      </div>

      <Card className="p-4">
        <SectionHeader eyebrow="Curated offers" title="Cross-school marketplace"
          right={
            <div className="flex items-center gap-2">
              <Select value={cat} onValueChange={setCat}>
                <SelectTrigger className="h-8 w-40 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="all">All categories</SelectItem>{cats.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
              <Button size="sm" onClick={()=>toast.success("Sourcing brief published to vendors")}><Sparkles className="mr-1 h-3 w-3" />Post RFQ</Button>
            </div>
          } />
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
          {filtered.map((o:any) => {
            const disc = o.original_ugx ? Math.round(((o.original_ugx - o.price_ugx)/o.original_ugx)*100) : 0;
            return (
              <div key={o.id} className="group relative overflow-hidden rounded-xl border bg-gradient-to-br from-card to-muted/30 p-4 transition hover:shadow-md">
                {o.badge && <span className="absolute right-2 top-2 rounded-full bg-[var(--forest)] px-2 py-0.5 text-[9px] font-medium text-primary-foreground">{o.badge}</span>}
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{o.category}</div>
                <div className="mt-1 font-display text-base leading-tight">{o.title}</div>
                <div className="mt-0.5 text-[11px] text-muted-foreground">{o.merchant_name}</div>
                <div className="mt-3 flex items-baseline gap-2">
                  <span className="font-display text-xl">{fmtUGX(o.price_ugx)}</span>
                  {o.original_ugx && <span className="text-xs text-muted-foreground line-through">{fmtUGX(o.original_ugx)}</span>}
                  {disc > 0 && <Badge className="ml-auto bg-emerald-500/15 text-emerald-600" variant="secondary">−{disc}%</Badge>}
                </div>
                <div className="mt-3 flex gap-2">
                  <Button size="sm" className="flex-1" onClick={()=>toast.success(`Ordered: ${o.title}`)}>Order</Button>
                  <Button size="sm" variant="outline" onClick={()=>toast.success("Added to comparison")}><Filter className="h-3 w-3" /></Button>
                </div>
              </div>
            );
          })}
          {!filtered.length && <div className="col-span-full p-6 text-center text-sm text-muted-foreground">No offers in this category yet.</div>}
        </div>
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card className="p-4">
          <SectionHeader eyebrow="Bulk pipeline" title="RFQs & purchase orders" />
          <div className="max-h-[300px] divide-y overflow-auto text-sm">
            {bulk.map((b:any) => (
              <div key={b.id} className="grid grid-cols-[1.4fr_1fr_1fr_auto] items-center gap-2 py-2.5">
                <div><div className="font-medium">{b.po_number ?? b.id.slice(0,8)}</div><div className="text-[10px] text-muted-foreground">{b.notes ?? "—"}</div></div>
                <div className="font-mono text-xs">{fmtUGX(b.quote_ugx)}</div>
                <div className="text-[11px] text-muted-foreground">{b.delivery_window}</div>
                <Badge variant="outline" className="text-[10px] capitalize">{b.status}</Badge>
              </div>
            ))}
            {!bulk.length && <div className="p-6 text-center text-muted-foreground">No bulk orders yet.</div>}
          </div>
        </Card>
        <Card className="p-4">
          <SectionHeader eyebrow="Meal plans" title="Menu program" />
          <div className="max-h-[300px] divide-y overflow-auto text-sm">
            {menus.map((m:any) => (
              <div key={m.id} className="py-2.5">
                <div className="flex items-center justify-between"><span className="font-medium capitalize">{m.meal} · {shortDate(m.for_date)}</span><Badge variant="outline" className="text-[10px]">{Array.isArray(m.items) ? m.items.length : 0} items</Badge></div>
                <div className="mt-1 flex flex-wrap gap-1">{(Array.isArray(m.items) ? m.items : []).slice(0,4).map((it:any, i:number) => (
                  <span key={i} className="rounded-full border bg-muted/40 px-2 py-0.5 text-[10px]">{it.name} · {fmtUGX(it.price_ugx)}</span>
                ))}</div>
              </div>
            ))}
            {!menus.length && <div className="p-6 text-center text-muted-foreground">No menus published.</div>}
          </div>
        </Card>
      </div>
    </div>
  );
}

/* ============================================================ */
/*                    REPORTS                                    */
/* ============================================================ */
export function ReportsPanel({ schoolId }: { schoolId: string }) {
  const { data: students = [] } = useQuery({ queryKey: ["rep-students", schoolId], queryFn: async () => (await supabase.from("students").select("id, class_label, boarding, balance_ugx").eq("school_id", schoolId)).data ?? [] });
  const { data: settlements = [] } = useQuery({ queryKey: ["rep-settlements", schoolId], queryFn: async () => (await supabase.from("settlements").select("*").eq("school_id", schoolId).order("created_at", { ascending: false }).limit(120)).data ?? [] });
  const { data: fees = [] } = useQuery({ queryKey: ["rep-fees", schoolId, students.length], enabled: students.length > 0, queryFn: async () => (await supabase.from("student_fee_terms").select("total_ugx, paid_ugx").in("student_id", students.map(s=>s.id))).data ?? [] });

  const monthly = useMemo(() => {
    const m = new Map<string, { m: string; gross: number; net: number; count: number }>();
    settlements.forEach((s:any) => {
      const d = new Date(s.batch_date ?? s.created_at); const k = d.toISOString().slice(0,7);
      if (!m.has(k)) m.set(k, { m: d.toLocaleString("en-UG",{month:"short"}), gross: 0, net: 0, count: 0 });
      const b = m.get(k)!; b.gross += Number(s.gross_ugx||0); b.net += Number(s.net_ugx||0); b.count += Number(s.tx_count||0);
    });
    return Array.from(m.values()).reverse();
  }, [settlements]);

  const reports = [
    { id: "enrollment", title: "Enrollment census", desc: "Class · gender · boarding split", icon: Users, count: students.length, format: "PDF · CSV" },
    { id: "collections", title: "Fee collections", desc: "Term aging + autopay adoption", icon: Banknote, count: fees.length, format: "PDF · XLSX" },
    { id: "settlements", title: "Settlement statement", desc: "Merchant net · fees · variance", icon: Landmark, count: settlements.length, format: "CSV · JSON" },
    { id: "attendance", title: "Attendance audit", desc: "Gate + classroom + welfare flags", icon: ClipboardList, count: 0, format: "PDF" },
    { id: "welfare", title: "Welfare & health", desc: "Sick-bay · nutrition · immunization", icon: Heart, count: 0, format: "PDF" },
    { id: "transport", title: "Transport telematics", desc: "On-time · km · fuel · incidents", icon: Gauge, count: 0, format: "PDF · CSV" },
    { id: "ledger", title: "Ledger export · 10-year", desc: "Immutable wallet_ledger_entries_v2", icon: FileText, count: 0, format: "Signed CSV" },
    { id: "compliance", title: "Regulator pack", desc: "URA · KCCA · MoES bundle", icon: ShieldCheck, count: 0, format: "PDF" },
  ];

  return (
    <div className="space-y-4">
      <Card className="p-4">
        <SectionHeader eyebrow="Trend" title="Settlement rhythm · by month" />
        <div className="h-64">
          <ResponsiveContainer>
            <BarChart data={monthly}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.2} /><XAxis dataKey="m" tick={{fontSize:10}} /><YAxis tick={{fontSize:10}} tickFormatter={(v)=>`${(v/1e6).toFixed(1)}M`} /><Tooltip formatter={(v:any)=>fmtUGX(v)} />
              <Bar dataKey="gross" name="Gross" fill="#c9a84c" radius={[6,6,0,0]} />
              <Bar dataKey="net" name="Net" fill="#0d7a5f" radius={[6,6,0,0]} />
              <Legend wrapperStyle={{fontSize:10}} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
        {reports.map(r => (
          <Card key={r.id} className="group relative overflow-hidden p-4 transition hover:shadow-md">
            <div className="absolute inset-x-0 top-0 h-0.5 bg-gradient-to-r from-[var(--forest)] via-[var(--ochre)] to-[var(--forest)]" />
            <div className="flex items-start justify-between">
              <div className="grid h-9 w-9 place-items-center rounded-md bg-[color-mix(in_oklab,var(--forest)_10%,transparent)] text-[var(--forest)]"><r.icon className="h-4 w-4" /></div>
              <Badge variant="outline" className="text-[9px]">{r.format}</Badge>
            </div>
            <div className="mt-3 font-display text-base leading-tight">{r.title}</div>
            <div className="mt-0.5 text-[11px] text-muted-foreground">{r.desc}</div>
            {r.count > 0 && <div className="mt-2 text-[10px] text-muted-foreground">Draws from {r.count.toLocaleString()} records</div>}
            <div className="mt-3 flex gap-2">
              <Button size="sm" className="flex-1" onClick={()=>toast.success(`${r.title} generated`)}><Download className="mr-1 h-3 w-3" />Generate</Button>
              <Button size="sm" variant="outline" onClick={()=>toast.success("Scheduled monthly")}><Calendar className="h-3 w-3" /></Button>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

/* ============================================================ */
/*                    SETTINGS & RBAC                            */
/* ============================================================ */
export function SettingsRbacPanel({ schoolId }: { schoolId: string }) {
  const qc = useQueryClient();
  const { data: school } = useQuery({ queryKey: ["srb-school", schoolId], queryFn: async () => (await supabase.from("schools").select("*").eq("id", schoolId).maybeSingle()).data });
  const { data: roles = [] } = useQuery({
    queryKey: ["srb-roles", schoolId],
    queryFn: async () => (await supabase.from("user_roles").select("id, user_id, role, created_at").eq("school_id", schoolId)).data ?? [],
  });
  const userIds = Array.from(new Set(roles.map((r:any) => r.user_id)));
  const { data: profiles = [] } = useQuery({
    queryKey: ["srb-profiles", userIds.length],
    enabled: userIds.length > 0,
    queryFn: async () => (await supabase.from("profiles").select("id, full_name, phone").in("id", userIds as any)).data ?? [],
  });

  const [editName, setEditName] = useState(school?.name ?? "");
  const [editDistrict, setEditDistrict] = useState((school as any)?.district ?? "");
  const [editEmail, setEditEmail] = useState((school as any)?.email ?? "");

  const save = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.from("schools").update({ name: editName, district: editDistrict, email: editEmail } as any).eq("id", schoolId);
      if (error) throw error;
    },
    onSuccess: () => { toast.success("School profile saved"); qc.invalidateQueries({ queryKey: ["srb-school"] }); qc.invalidateQueries({ queryKey: ["my-school"] }); },
    onError: (e: Error) => toast.error(e.message),
  });

  const rbacMatrix = [
    { role: "school_admin", modules: ["all"], desc: "Full console access" },
    { role: "teacher", modules: ["Academics", "Attendance"], desc: "Timetable, grades, roll call" },
    { role: "bursar", modules: ["Fees", "Wallets", "Reports"], desc: "Treasury workflows" },
    { role: "matron", modules: ["Boarding", "Health"], desc: "Boarding & sick-bay" },
    { role: "dispatch", modules: ["Transport"], desc: "Fleet, drivers, routes" },
    { role: "procurement", modules: ["Procurement", "Marketplace"], desc: "PO + vendor management" },
  ];

  return (
    <div className="space-y-4">
      <div className="grid gap-4 lg:grid-cols-[1.2fr_1fr]">
        <Card className="p-4">
          <SectionHeader eyebrow="Institution" title="School profile" />
          <div className="grid gap-3 md:grid-cols-2">
            <div><label className="text-[10px] uppercase tracking-wider text-muted-foreground">Name</label><Input value={editName} onChange={(e)=>setEditName(e.target.value)} /></div>
            <div><label className="text-[10px] uppercase tracking-wider text-muted-foreground">District</label><Input value={editDistrict} onChange={(e)=>setEditDistrict(e.target.value)} /></div>
            <div className="md:col-span-2"><label className="text-[10px] uppercase tracking-wider text-muted-foreground">Contact email</label><Input value={editEmail} onChange={(e)=>setEditEmail(e.target.value)} type="email" /></div>
          </div>
          <div className="mt-3 flex justify-end gap-2">
            <Button size="sm" variant="ghost" onClick={()=>{setEditName(school?.name??"");setEditDistrict((school as any)?.district??"");setEditEmail((school as any)?.email??"");}}>Reset</Button>
            <Button size="sm" onClick={()=>save.mutate()} disabled={save.isPending}>{save.isPending ? "Saving…" : "Save profile"}</Button>
          </div>
        </Card>

        <Card className="p-4">
          <SectionHeader eyebrow="Regulation" title="Compliance switches" />
          <div className="divide-y">
            {[
              { k: "Two-factor for admins", v: true, desc: "Require TOTP on sign-in" },
              { k: "Maker-checker for large refunds", v: true, desc: "Above UGX 500,000" },
              { k: "10-year ledger retention", v: true, desc: "Immutable · regulator-mandated" },
              { k: "Data export on request", v: true, desc: "DPA subject access enabled" },
              { k: "Vendor KYC required", v: true, desc: "Before onboarding merchant" },
              { k: "Parent visibility on wallet trail", v: false, desc: "Show child ledger to guardian" },
            ].map((s,i) => (
              <div key={i} className="flex items-center justify-between py-2.5">
                <div><div className="text-sm">{s.k}</div><div className="text-[10px] text-muted-foreground">{s.desc}</div></div>
                <Switch defaultChecked={s.v} onCheckedChange={()=>toast.success(`${s.k} · updated`)} />
              </div>
            ))}
          </div>
        </Card>
      </div>

      <Card className="p-4">
        <SectionHeader eyebrow="RBAC matrix" title="Roles & module access"
          right={<Button size="sm" variant="outline" onClick={()=>toast.success("Role definitions exported")}><Download className="mr-1 h-3 w-3" />Export matrix</Button>} />
        <div className="grid gap-2">
          {rbacMatrix.map(r => (
            <div key={r.role} className="grid grid-cols-[160px_1fr_auto] items-center gap-3 rounded-lg border bg-card p-3">
              <div className="flex items-center gap-2"><KeyRound className="h-4 w-4 text-[var(--forest)]" /><span className="font-mono text-xs">{r.role}</span></div>
              <div><div className="text-sm">{r.desc}</div><div className="mt-1 flex flex-wrap gap-1">{r.modules.map(m => <Badge key={m} variant="secondary" className="text-[9px]">{m}</Badge>)}</div></div>
              <Button size="sm" variant="ghost" onClick={()=>toast.success(`Editing ${r.role}`)}><Edit3 className="h-3 w-3" /></Button>
            </div>
          ))}
        </div>
      </Card>

      <Card>
        <div className="flex items-center justify-between border-b p-4">
          <div className="font-display text-lg">Staff with console access · {roles.length}</div>
          <Button size="sm" onClick={()=>toast.success("Invite composer open")}><UserPlus className="mr-1 h-3 w-3" />Invite staff</Button>
        </div>
        <div className="max-h-[380px] divide-y overflow-auto text-sm">
          {roles.map((r:any) => {
            const p = profiles.find((x:any) => x.id === r.user_id);
            return (
              <div key={r.id} className="grid grid-cols-[36px_1.4fr_1fr_1fr_auto] items-center gap-3 p-3">
                <div className="grid h-9 w-9 place-items-center rounded-full bg-muted text-xs font-semibold">{(p?.full_name ?? "?").split(" ").map((s:string)=>s[0]).slice(0,2).join("")}</div>
                <div><div className="font-medium">{p?.full_name ?? "Unnamed"}</div><div className="text-[10px] text-muted-foreground">{p?.phone ?? "—"}</div></div>
                <Badge variant="outline" className="justify-center text-[10px] font-mono">{r.role}</Badge>
                <div className="text-[10px] text-muted-foreground">Since {shortDate(r.created_at)}</div>
                <div className="flex gap-1">
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={()=>toast.success("Role revoked")}><Trash2 className="h-3 w-3 text-rose-500" /></Button>
                </div>
              </div>
            );
          })}
          {!roles.length && <div className="p-6 text-center text-muted-foreground">No staff assigned yet.</div>}
        </div>
      </Card>
    </div>
  );
}
